import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Snackbar from '@mui/material/Snackbar';
import axios from 'axios';
import React, { useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useNavigate } from 'react-router-dom';
import Typography from './Modules/Components/Typography';
import FormButton from './Modules/Form/FormButton';
import FormFeedback from './Modules/Form/FormFeedback';
import RFTextField from './Modules/Form/RFTextField';
import { emailV, passwordValidate, required } from './Modules/Form/validation';
import AppFooter from './Modules/Views/AppFooter';
import AppForm from './Modules/Views/AppForm';
import { AppNavBar } from './Modules/Views/AppNavBar';
import withRoot from './Modules/withRoot';

const SignUp = () => {
  const [sent, setSent] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const validate = (values: { [index: string]: string }) => {
    const errors = required(
      ['firstName', 'lastName', 'email', 'password', 'confirmPassword'],
      values
    );

    if (!errors.email) {
      const emailError = emailV(values.email);
      if (emailError) {
        errors.email = emailError;
      }
    }
    if (!errors.password) {
      const passwordError = passwordValidate(values.password);
      if (passwordError) {
        errors.password = passwordError;
      }
    }
    if (!errors.confirmPassword && values.password !== values.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match.';
      setError('Passwords do not match.');
      setSnackbarOpen(true); // Open snackbar with the error message
    }

    return errors;
  };

  const handleSubmit = async (values: any) => {
    setSent(true);
  
    // If passwords don't match, prevent form submission
    if (values.password !== values.confirmPassword) {
      setError('Passwords do not match.');
      setSnackbarOpen(true);
      setSent(false);
      return;
    }
  
    try {
      console.log('Submitted Values:', values);
  
      const payload = {
        firstName: values.firstName,
        lastName: values.lastName,
        emailAddress: values.email,
        password: values.password,
        passwordConfirmation: values.confirmPassword,
      };

  const response = await axios.post('https://ipms.insollex.com/api/register', payload, {
  withCredentials: true,
});




  
      if (response.status === 201) {
        navigate('/signIn', { replace: true });
      } else {
        alert('Unexpected response. Please try again.');
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      if (error.response) {
        // If the error is from the server, pass the error message to the state
        setError(error.response.data.message || 'Registration failed.');
        setSnackbarOpen(true);  // Open the Snackbar with the error message
      } else {
        setError('An unexpected error occurred. Please try again.');
        setSnackbarOpen(true);
      }
    } finally {
      setSent(false);
    }
  };
  
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <React.Fragment>
      <AppNavBar />
      <AppForm>
        <Typography variant="h3" gutterBottom marked="center" align="center">
          Sign Up
        </Typography>
        <Form
          onSubmit={handleSubmit} // React Final Form Props
          subscription={{ submitting: true, submitError: true }} // Subscribing to both submitting and submitError
          validate={validate}
        >
          {({ handleSubmit: handleSubmit2, submitting, submitError }) => (
            <Box component="form" onSubmit={handleSubmit2} noValidate sx={{ mt: 6 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Field
                    autoFocus
                    component={RFTextField}
                    disabled={submitting || sent}
                    autoComplete="given-name"
                    fullWidth
                    label="First name"
                    name="firstName"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    component={RFTextField}
                    disabled={submitting || sent}
                    autoComplete="family-name"
                    fullWidth
                    label="Last name"
                    name="lastName"
                    required
                  />
                </Grid>
              </Grid>
              <Field
                autoComplete="email"
                component={RFTextField}
                disabled={submitting || sent}
                fullWidth
                label="Email"
                margin="normal"
                name="email"
                type="email"
                required
              />
              <Field
                fullWidth
                component={RFTextField}
                disabled={submitting || sent}
                required
                name="password"
                label="Password"
                type="password"
                margin="normal"
              />
              <Field
                fullWidth
                component={RFTextField}
                disabled={submitting || sent}
                required
                name="confirmPassword"
                label="Confirm Password"
                type="password"
                margin="normal"
              />
              {submitError && (
                <FormFeedback error sx={{ mt: 2 }}>
                  {submitError}
                </FormFeedback>
              )}
              <FormButton sx={{ mt: 3, mb: 2 }} disabled={submitting || sent} color="secondary" fullWidth>
                {submitting || sent ? 'In progress…' : 'Sign Up'}
              </FormButton>
            </Box>
          )}
        </Form>
        <Typography variant="body2" align="center">
          <Link href="/signin/" underline="always">
            Already have an account?
          </Link>
        </Typography>
      </AppForm>
      <AppFooter />

      {/* Snackbar for error messages */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="error"
          sx={{
            backgroundColor: '#f8d7da',
            color: 'red',
            padding: '15px 25px',
            borderRadius: '5px',
            fontWeight: 'bold',
            fontSize: '14px',
            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
            border: '1px solid #f5c6cb',
            marginTop: '230px', 
          }}
        >
          {error}
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
};

export default withRoot(SignUp);